import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

export type SkeletonShape = 'line' | 'rect' | 'circle';

export interface SkeletonProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'className'> {
  shape?: SkeletonShape;
  width?: number | string;
  height?: number | string;
  lines?: number;
  className?: string;
}

function lengthToCss(value: number | string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return typeof value === 'number' ? `${String(value)}px` : value;
}

/** Loading placeholder with shimmer sweep.
 *  Use `lines` for multi-line text placeholders; each line narrows progressively. */
export function Skeleton(props: SkeletonProps): ReactNode {
  const { shape = 'line', width, height, lines = 1, className, ...rest } = props;
  const classes = ['vq-skeleton', className].filter(Boolean).join(' ');
  const resolvedWidth = lengthToCss(width);
  const resolvedHeight = lengthToCss(height);
  const style: CSSProperties = {};
  if (resolvedWidth !== undefined) style.width = resolvedWidth;
  if (resolvedHeight !== undefined) style.height = resolvedHeight;

  if (lines <= 1) {
    return (
      <span
        {...rest}
        className={classes}
        data-component="Skeleton"
        data-shape={shape}
        aria-hidden="true"
        style={style}
      />
    );
  }

  const rows: number[] = [];
  for (let i = 0; i < lines; i += 1) rows.push(i);
  return (
    <span
      className="vq-skeleton-group"
      data-component="Skeleton"
      data-shape="line"
      aria-hidden="true"
    >
      {rows.map((i) => {
        const rowStyle: CSSProperties = { ...style };
        if (i === rows.length - 1) rowStyle.width = '62%';
        return <span key={i} className={classes} data-shape="line" style={rowStyle} />;
      })}
    </span>
  );
}
